/**
 * Pending pages slice (SCRUM-1951, RTK bundle 2) — the former switch reducer
 * pendingPagesReducer.js as createSlice. Behaviour-preserving: every case
 * answers the same legacy action type string (pendingPagesTypes.js) that
 * pendingPagesActions.js already dispatches, and returns the same state.
 * The actions module is unchanged, so no importer changes.
 * Characterised by tests/integration/reducers/reviewPublishingReducers.characterisation.test.js.
 */
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import {
  GET_PENDING_PAGES,
  ADD_PENDING_PAGE,
  CLEAR_MESSAGE,
  PUBLISH,
  GET_NON_PUBLISHED_PAGES,
  GET_PUBLISHED_PAGES,
  DELETE,
} from "./pendingPagesTypes";
import type { PendingPageAddResponse } from "../../../../shared/apiTypes";

type PendingPagesState = {
  pendingPages: any[];
  message: string;
  nonPublishedPages: any[];
  publishedPages: any[];
};

const initialState: PendingPagesState = {
  pendingPages: [],
  message: "",
  nonPublishedPages: [],
  publishedPages: [],
};

const pendingPagesSlice = createSlice({
  name: "pendingPages",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(CLEAR_MESSAGE, (state) => ({ ...state, message: "" }))
      .addCase(DELETE, (state, action: PayloadAction<{ _id: string; published?: boolean }, typeof DELETE>) =>
        action.payload.published
          ? {
              ...state,
              publishedPages: state.publishedPages.filter((page) => page._id !== action.payload._id),
            }
          : {
              ...state,
              nonPublishedPages: state.nonPublishedPages.filter((page) => page._id !== action.payload._id),
            }
      )
      .addCase(ADD_PENDING_PAGE, (state, action: PayloadAction<PendingPageAddResponse, typeof ADD_PENDING_PAGE>) => ({
        ...state,
        message: action.payload.msg,
      }))
      .addCase(GET_PENDING_PAGES, (state, action: PayloadAction<PendingPagesState["pendingPages"], typeof GET_PENDING_PAGES>) => ({
        ...state,
        pendingPages: action.payload,
      }))
      .addCase(GET_NON_PUBLISHED_PAGES, (state, action: PayloadAction<PendingPagesState["nonPublishedPages"], typeof GET_NON_PUBLISHED_PAGES>) => ({
        ...state,
        nonPublishedPages: action.payload,
      }))
      .addCase(GET_PUBLISHED_PAGES, (state, action: PayloadAction<PendingPagesState["publishedPages"], typeof GET_PUBLISHED_PAGES>) => ({
        ...state,
        publishedPages: action.payload,
      }))
      // Moves the page to the other list and flips `published`; an unknown id
      // leaves the state as it is.
      .addCase(PUBLISH, (state, action: PayloadAction<{ msg: string; id: string }, typeof PUBLISH>) => {
        const pageToPublish = state.nonPublishedPages.find((page) => page._id === action.payload.id);
        const pageToUnpublish = state.publishedPages.find((page) => page._id === action.payload.id);
        if (pageToPublish) {
          return {
            ...state,
            message: action.payload.msg,
            publishedPages: [{ ...pageToPublish, published: true }, ...state.publishedPages],
            nonPublishedPages: state.nonPublishedPages.filter((page) => page._id !== action.payload.id),
          };
        }
        if (pageToUnpublish) {
          return {
            ...state,
            message: action.payload.msg,
            nonPublishedPages: [{ ...pageToUnpublish, published: false }, ...state.nonPublishedPages],
            publishedPages: state.publishedPages.filter((page) => page._id !== action.payload.id),
          };
        }
        return state;
      });
  },
});

export default pendingPagesSlice.reducer;
